import {useEffect, useMemo, useRef, useState} from "react";
import {Link} from "react-router";
import {
  Check,
  Clipboard,
  Database,
  Eye,
  KeyRound,
  Link2,
  LoaderCircle,
  MailCheck,
  PencilLine,
  Plus,
  Radio,
  Rocket,
  Send,
  Trash2,
  Users,
} from "lucide-react";
import {trpc} from "@/admin/lib/trpc";
import {useSession} from "@/admin/lib/auth-client";
import {credentialTemplate, readOnboardDraft, writeOnboardDraft} from "@/admin/lib/easyOnboardDraft";
import {
  buildEasyOnboardEmailContent,
  EASY_ONBOARD_API_KEY_PLACEHOLDER,
  EASY_ONBOARD_AWS_ACCESS_KEY_PLACEHOLDER,
  EASY_ONBOARD_AWS_SECRET_KEY_PLACEHOLDER,
  trialProductLabel,
  validTrialAccess,
  type EasyOnboardProduct,
  type EasyOnboardBucket,
  type EasyOnboardRecipient,
} from "@/shared/lib/easyOnboardEmailDraft";
import {renderEditableEmailHtml} from "@/shared/lib/easyOnboardEmailFormat";

type EmailDeliveryRecovery = {
  status: "not-started" | "pending" | "failed" | "sent" | "unknown";
  error: string | null;
  sentAt: string | null;
};

type CopyTarget = "api" | "aws" | null;
type EmailView = "preview" | "edit";
type OrganizationMode = "new" | "existing";
type RecipientRow = {id: string; name: string; email: string};
type CcRow = {id: string; email: string};

const DEFAULT_CC = ["matthew@nosible.com", "gareth@nosible.com", "henry@nosible.com"];
const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;

function rowId(): string {
  return crypto.randomUUID();
}

function blankRecipient(): RecipientRow {
  return {id: rowId(), name: "", email: ""};
}

function getGmailReturnStatus(): string | null {
  return new URLSearchParams(window.location.search).get("easy_onboard");
}

function uniqueCc(
  defaults: readonly string[],
  customCc: CcRow[],
  recipients: EasyOnboardRecipient[],
): string[] {
  const recipientEmails = new Set(
    recipients.map((recipient) => recipient.email.trim().toLowerCase()),
  );
  return [...defaults, ...customCc.map(({email}) => email)]
    .map((email) => email.trim().toLowerCase())
    .filter(
      (email, index, emails) =>
        email &&
        !recipientEmails.has(email) &&
        emails.indexOf(email) === index,
    );
}

function replaceCredentialPlaceholders(
  body: string,
  result: {
    apiKey: string | null;
    awsAccessKeyId: string | null;
    awsSecretAccessKey: string | null;
  },
): string {
  return body
    .replaceAll(
      EASY_ONBOARD_API_KEY_PLACEHOLDER,
      result.apiKey ?? EASY_ONBOARD_API_KEY_PLACEHOLDER,
    )
    .replaceAll(
      EASY_ONBOARD_AWS_SECRET_KEY_PLACEHOLDER,
      result.awsSecretAccessKey ?? EASY_ONBOARD_AWS_SECRET_KEY_PLACEHOLDER,
    )
    .replaceAll(
      EASY_ONBOARD_AWS_ACCESS_KEY_PLACEHOLDER,
      result.awsAccessKeyId ?? EASY_ONBOARD_AWS_ACCESS_KEY_PLACEHOLDER,
    );
}

export function EasyOnboardPage() {
  const {data: session} = useSession();
  return session?.user.id ? <EasyOnboardForm key={session.user.id} adminUserId={session.user.id} /> : null;
}

function EasyOnboardForm({adminUserId}: {adminUserId: string}) {
  const [initial] = useState(() => {
    try {return {draft: readOnboardDraft(sessionStorage, adminUserId), error: null};}
    catch {return {draft: null, error: "Your saved trial draft could not be read. Check existing trials before starting another request, then clear this draft."};}
  });
  const saved = initial.draft;
  const [storageError, setStorageError] = useState<string | null>(initial.error);
  const [requestId, setRequestId] = useState(() => saved?.requestId ?? rowId());
  const [attempted, setAttempted] = useState(saved?.attempted ?? false);
  const submitInFlight = useRef(false);
  const gmailReturnStatus = getGmailReturnStatus();
  const [previewStartedAt] = useState(() => new Date());
  const [recipients, setRecipients] = useState<RecipientRow[]>(saved?.recipients ?? [blankRecipient()]);
  const [customCc, setCustomCc] = useState<CcRow[]>(saved?.customCc ?? []);
  const [organizationMode, setOrganizationMode] =
    useState<OrganizationMode>(saved?.organizationMode ?? "new");
  const [organizationName, setOrganizationName] = useState(saved?.organizationName ?? "");
  const [organizationId, setOrganizationId] = useState(saved?.organizationId ?? "");
  const [product, setProduct] = useState<EasyOnboardProduct>(saved?.product ?? "world-atlas");
  const [webApi, setWebApi] = useState(saved?.webApi ?? true);
  const [datasetMode, setDatasetMode] = useState<"none" | "static" | "live">(saved?.datasetMode ?? "none");
  const [trialDaysInput, setTrialDaysInput] = useState(saved?.trialDaysInput ?? "90");
  const [copied, setCopied] = useState<CopyTarget>(null);
  const [emailView, setEmailView] = useState<EmailView>("preview");
  const [emailSubject, setEmailSubject] = useState<string | null>(saved?.emailSubject ?? null);
  const [emailBody, setEmailBody] = useState<string | null>(saved?.emailBody ?? null);
  const [sendConfirmationOpen, setSendConfirmationOpen] = useState(false);
  const [deliveryRecovery, setDeliveryRecovery] = useState<EmailDeliveryRecovery | null>(null);

  const options = trpc.admin.easyOnboardOptions.useQuery(undefined, {
    staleTime: 60_000,
  });
  const gmailStatus = trpc.admin.gmailStatus.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: true,
  });
  const connectGmail = trpc.admin.connectGmail.useMutation({
    onSuccess: ({url}) => {if (persistDraft()) window.location.assign(url);},
  });
  const onboard = trpc.admin.easyOnboard.useMutation();
  const sendEmail = trpc.admin.sendEasyOnboardEmail.useMutation();
  const {mutateAsync: checkDeliveryStatus} = trpc.admin.easyOnboardEmailStatus.useMutation();
  const recovery = trpc.admin.easyOnboardRecovery.useQuery({requestId}, {
    enabled: !onboard.data,
    retry: false,
    refetchInterval: query => !attempted || query.state.data?.status === "created" || query.state.data?.status === "failed" || query.state.data?.status === "review" || query.state.error ? false : 2000,
  });
  const result = onboard.data ?? (attempted ? recovery.data?.result ?? undefined : undefined);
  const persistDraft = (hasAttempted = attempted) => {
    if (initial.error && storageError) return false;
    try {
      writeOnboardDraft(sessionStorage, adminUserId, {
        version: 1, requestId, attempted: hasAttempted, recipients, customCc,
        organizationMode, organizationName, organizationId, product, webApi, datasetMode,
        trialDaysInput, emailSubject: credentialTemplate(emailSubject, result), emailBody: credentialTemplate(emailBody, result),
      });
      setStorageError(null);
      return true;
    } catch {
      setStorageError("Your browser could not save this draft. Access creation and Gmail sign-in are paused so the request cannot be lost. Enable session storage and try again.");
      return false;
    }
  };
  useEffect(() => {persistDraft();}, [requestId, attempted, recipients, customCc, organizationMode, organizationName, organizationId, product, webApi, datasetMode, trialDaysInput, emailSubject, emailBody, result]);
  const transportFailure = Boolean(sendEmail.error && !sendEmail.error.data);
  const recoveringDelivery = transportFailure && (deliveryRecovery === null || deliveryRecovery.status === "pending");
  const deliveryUnknown = deliveryRecovery?.status === "unknown";
  const emailSent = Boolean(sendEmail.data) || deliveryRecovery?.status === "sent";
  const sendingEmail = sendEmail.isPending || recoveringDelivery || deliveryRecovery?.status === "pending" || (Boolean(result) && deliveryRecovery === null);
  const deliveryError = deliveryUnknown ? "Delivery could not be confirmed. Check Gmail Sent before taking any further action." : transportFailure
    ? deliveryRecovery?.status === "failed"
      ? deliveryRecovery.error ?? "Verification failed. Email was not sent."
      : deliveryUnknown
        ? "Delivery could not be confirmed. Check Gmail Sent before taking any further action."
        : deliveryRecovery?.status === "not-started"
          ? "The email request did not reach the server. Email was not sent; you can retry."
          : "The connection was interrupted. Checking the server's delivery status..."
    : sendEmail.error?.message;

  useEffect(() => {
    const emailToken = result?.emailToken;
    if (!transportFailure || !emailToken) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const status = await checkDeliveryStatus({emailToken});
        if (cancelled) return;
        setDeliveryRecovery(status);
        if (status.status !== "pending") return;
      } catch (error) {
        if (cancelled) return;
        // A structured API error needs user attention; transport errors can
        // recover. This read-only request never resubmits the send mutation.
        if (error && typeof error === "object" && "data" in error && error.data) {
          setDeliveryRecovery({status: "unknown", error: null, sentAt: null});
          return;
        }
      }
      timer = setTimeout(() => {void poll();}, 2_000);
    };
    void poll();
    return () => {cancelled = true; clearTimeout(timer);};
  }, [transportFailure, result?.emailToken, checkDeliveryStatus]);
  useEffect(() => {
    if (!result?.emailToken) return;
    let cancelled = false;
    setDeliveryRecovery({status: "pending", error: null, sentAt: null});
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const status = await checkDeliveryStatus({emailToken: result.emailToken});
        if (cancelled) return;
        setDeliveryRecovery(status);
        if (status.status !== "pending") return;
      } catch (error) {
        if (cancelled) return;
        if (error && typeof error === "object" && "data" in error && error.data) {
          setDeliveryRecovery({status: "unknown", error: null, sentAt: null});
          return;
        }
      }
      timer = setTimeout(() => {void poll();}, 2000);
    };
    void poll();
    return () => {cancelled = true; clearTimeout(timer);};
  }, [result?.emailToken, checkDeliveryStatus]);
  const canRetryCreation = !onboard.isPending && !recovery.isFetching && (recovery.data?.status === "not-started" || recovery.data?.status === "failed");
  const locked = Boolean(result) || onboard.isPending || (attempted && !canRetryCreation);
  const gmail = gmailStatus.data;
  const gmailReady = Boolean(gmail?.allowed && gmail.connected);
  const staticS3 = datasetMode === "static";
  const liveS3 = datasetMode === "live";
  const selectedProducts = product === "world-atlas" ? ["world", "atlas"] as const : [product];
  const staticAvailable = selectedProducts.every((selected) => Boolean(options.data?.datasets[selected].staticBuckets.length));
  const liveAvailable = selectedProducts.every((selected) => Boolean(options.data?.datasets[selected].liveBuckets.length));
  const datasetAvailable = !staticS3 && !liveS3 || (staticS3 ? staticAvailable : liveAvailable);
  const trialDays = Number(trialDaysInput);
  const trialDaysValid =
    trialDaysInput.trim() !== "" &&
    Number.isInteger(trialDays) &&
    trialDays >= 1 &&
    trialDays <= 365;
  const initialTrialDaysApplied = useRef(Boolean(saved));

  useEffect(() => {
    if (!options.data || initialTrialDaysApplied.current) return;
    initialTrialDaysApplied.current = true;
    setTrialDaysInput(String(options.data.trialDays));
  }, [options.data]);

  const previewRecipients = useMemo<EasyOnboardRecipient[]>(
    () =>
      recipients.map((recipient, index) => ({
        name: recipient.name.trim() || `User ${index + 1}`,
        email: recipient.email.trim() || `user${index + 1}@example.com`,
      })),
    [recipients],
  );
  const existingOrganization = options.data?.organizations.find(
    (organization: {id: string; name: string}) =>
      organization.id === organizationId,
  );
  const previewOrganization =
    organizationMode === "new"
      ? organizationName.trim() || "Your Organization"
      : existingOrganization?.name ?? "Existing Organization";
  const previewExpiresAt = useMemo(
    () =>
      new Date(
        previewStartedAt.getTime() +
          (trialDaysValid ? trialDays : 90) * DAY_IN_MILLISECONDS,
      ),
    [previewStartedAt, trialDays, trialDaysValid],
  );
  const previewBuckets: EasyOnboardBucket[] = selectedProducts.flatMap((selected) =>
    options.data?.datasets[selected][liveS3 ? "liveBuckets" : "staticBuckets"] ?? [],
  );
  const generatedDraft = useMemo(
    () =>
      buildEasyOnboardEmailContent({
        product: result?.product ?? product,
        recipients: result?.recipients ?? previewRecipients,
        organization: result?.organizationName ?? previewOrganization,
        access: result?.access ?? {webApi, staticS3, liveS3},
        apiKey:
          result?.apiKey ??
          (webApi ? EASY_ONBOARD_API_KEY_PLACEHOLDER : null),
        awsAccessKeyId:
          result?.awsAccessKeyId ??
          (staticS3 || liveS3
            ? EASY_ONBOARD_AWS_ACCESS_KEY_PLACEHOLDER
            : null),
        awsSecretAccessKey:
          result?.awsSecretAccessKey ??
          (staticS3 || liveS3
            ? EASY_ONBOARD_AWS_SECRET_KEY_PLACEHOLDER
            : null),
        buckets: result?.buckets ?? previewBuckets,
        startsAt: result ? new Date(result.startsAt) : previewStartedAt,
        expiresAt: result ? new Date(result.expiresAt) : previewExpiresAt,
      }),
    [
      product,
      liveS3,
      previewBuckets,
      previewExpiresAt,
      previewOrganization,
      previewRecipients,
      previewStartedAt,
      result,
      staticS3,
      webApi,
    ],
  );
  const currentEmailSubject = emailSubject ?? generatedDraft.subject;
  const currentEmailBody = emailBody ?? generatedDraft.body;
  const previewTo = result?.email.to ?? previewRecipients.map(({email}) => email);
  const previewCc =
    result?.email.cc ??
    uniqueCc(
      options.data?.defaultCc ?? DEFAULT_CC,
      customCc,
      previewRecipients,
    );
  const draftInputKey = useMemo(
    () =>
      JSON.stringify({
        recipients: recipients.map(({name, email}) => ({name, email})),
        customCc: customCc.map(({email}) => email),
        organizationMode,
        organizationName,
        organizationId,
        product,
        webApi,
        datasetMode,
        trialDaysInput,
      }),
    [
      customCc,
      product,
      organizationId,
      organizationMode,
      organizationName,
      recipients,
      trialDaysInput,
      webApi,
      datasetMode,
    ],
  );
  const previousDraftInputKey = useRef(draftInputKey);
  const appliedResultToken = useRef<string | null>(null);

  useEffect(() => {
    if (
      previousDraftInputKey.current !== draftInputKey &&
      !result
    ) {
      setEmailSubject(null);
      setEmailBody(null);
      if (attempted && canRetryCreation && !onboard.isPending) {
        setRequestId(rowId()); setAttempted(false);
      }
    }
    previousDraftInputKey.current = draftInputKey;
  }, [draftInputKey, result]);

  useEffect(() => {
    if (!result || appliedResultToken.current === result.emailToken) return;
    appliedResultToken.current = result.emailToken;
    setEmailBody((current) =>
      current ? replaceCredentialPlaceholders(current, result) : null,
    );
  }, [result]);

  useEffect(() => {
    if (!gmailReturnStatus) return;
    const url = new URL(window.location.href);
    url.searchParams.delete("easy_onboard");
    window.history.replaceState({}, "", url);
    void gmailStatus.refetch();
  }, [gmailReturnStatus]);

  const copyText = async (target: Exclude<CopyTarget, null>, value: string) => {
    await navigator.clipboard.writeText(value);
    setCopied(target);
    window.setTimeout(() => setCopied(null), 1800);
  };

  const reset = () => {
    setRequestId(rowId());
    setAttempted(false);
    submitInFlight.current = false;
    setStorageError(null);
    onboard.reset();
    sendEmail.reset();
    setDeliveryRecovery(null);
    appliedResultToken.current = null;
    setRecipients([blankRecipient()]);
    setCustomCc([]);
    setOrganizationMode("new");
    setOrganizationName("");
    setOrganizationId("");
    setProduct("world-atlas");
    setWebApi(true);
    setDatasetMode("none");
    setTrialDaysInput(String(options.data?.trialDays ?? 90));
    setCopied(null);
    setEmailView("preview");
    setEmailSubject(null);
    setEmailBody(null);
    setSendConfirmationOpen(false);
    window.scrollTo({top: 0, behavior: "smooth"});
  };

  const updateRecipient = (
    id: string,
    field: "name" | "email",
    value: string,
  ) => {
    setRecipients((current) =>
      current.map((recipient) =>
        recipient.id === id ? {...recipient, [field]: value} : recipient,
      ),
    );
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (result || submitInFlight.current || (attempted && !canRetryCreation) || !persistDraft(true)) return;
    submitInFlight.current = true;
    setAttempted(true);
    onboard.mutate({
      requestId,
      product,
      recipients: recipients.map(({name, email}) => ({
        name: name.trim(),
        email: email.trim(),
      })),
      cc: customCc
        .map(({email}) => email.trim())
        .filter((email) => Boolean(email)),
      organization:
        organizationMode === "new"
          ? {mode: "new", name: organizationName.trim()}
          : {mode: "existing", id: organizationId},
      access: {webApi, staticS3, liveS3},
      trialDays,
    }, {onSettled: () => {submitInFlight.current = false; void recovery.refetch();}});
  };

  const handleSend = () => {
    if (!result || sendingEmail || emailSent || deliveryUnknown) return;
    sendEmail.reset();
    setSendConfirmationOpen(true);
  };

  const confirmSend = () => {
    if (!result || sendingEmail || emailSent || deliveryUnknown) return;
    setSendConfirmationOpen(false);
    sendEmail.mutate({
      emailToken: result.emailToken,
      subject: currentEmailSubject.trim(),
      body: currentEmailBody,
    });
  };

  const recipientsComplete = recipients.every(
    (recipient) => recipient.name.trim() && recipient.email.trim(),
  );
  const organizationComplete =
    organizationMode === "new"
      ? Boolean(organizationName.trim())
      : Boolean(organizationId);
  const emailCustomized = emailSubject !== null || emailBody !== null;
  const oauthNotice =
    gmailReturnStatus === "gmail_connected"
      ? "Gmail connected. Your current signature will be added when you send."
      : gmailReturnStatus === "gmail_denied"
        ? "Gmail authorization was cancelled. No mailbox access was granted."
        : gmailReturnStatus === "gmail_error"
          ? "Gmail could not be connected. Please try the authorization again."
          : null;

  return (
    <div className="easy-onboard-page">
      <header className="easy-onboard-page-header">
        <div>
          <span className="easy-onboard-kicker">
            {result ? (
              <>
                <Check aria-hidden="true" /> ACCESS CREATED
              </>
            ) : (
              <>
                <KeyRound aria-hidden="true" /> FLEXIBLE{" "}
                {trialDaysValid ? trialDays : "CUSTOM"}-DAY TRIAL
              </>
            )}
          </span>
          <h1>Easy onboard</h1>
          <p>
            Configure access on the left and review the exact email on the
            right. Credentials remain placeholders until you explicitly
            create access.
          </p>
        </div>
        <Link className="easy-onboard-back" to="/">
          BACK TO ADMIN
        </Link>
      </header>

      <div className="easy-onboard-workspace">
        <div className="easy-onboard-left">
          <section
            className={`easy-onboard-gmail-status${gmailReady ? " is-connected" : ""}`}
          >
            {gmailReady ? (
              <MailCheck aria-hidden="true" />
            ) : (
              <Link2 aria-hidden="true" />
            )}
            <div>
              <strong>
                {gmailReady ? "Gmail connected" : "Connect Stuart's Gmail"}
              </strong>
              <span>
                {gmailReady
                  ? gmail?.signatureConfigured
                    ? `${gmail?.email} will send from your inbox with your signature.`
                    : `${gmail?.email} will send from your inbox. Add a Gmail signature before sending.`
                  : gmail?.configured === false
                    ? "The server-side Gmail client is being configured."
                    : "You can configure and preview access now; Gmail is only required to send."}
              </span>
            </div>
            {!gmailReady && gmail?.configured && gmail.allowed ? (
              <button
                type="button"
                disabled={connectGmail.isPending || onboard.isPending || Boolean(storageError)}
                onClick={() => connectGmail.mutate()}
              >
                {connectGmail.isPending ? "OPENING..." : "CONNECT GMAIL"}
              </button>
            ) : null}
          </section>

          {oauthNotice ? (
            <p
              className={
                gmailReturnStatus === "gmail_connected"
                  ? "easy-onboard-success"
                  : "easy-onboard-error"
              }
              role="status"
            >
              {oauthNotice}
            </p>
          ) : null}

          {connectGmail.error ? (
            <p className="easy-onboard-error" role="alert">
              {connectGmail.error.message}
            </p>
          ) : null}

          {storageError || recovery.error ? <p className="easy-onboard-error" role="alert">{storageError ?? recovery.error?.message}</p> : null}
          {attempted && !result ? <p role="status">{canRetryCreation
            ? "No access has been created for this request. You can retry the same saved request."
            : recovery.data?.status === "review" ? "Access creation was interrupted and needs review. Check the trials page before taking further action; this request will not create additional credentials."
            : "Recovering your saved access request. Do not create another trial; if this remains unresolved, check the trials page before taking further action."}</p> : null}
          {!onboard.isPending && !result && (!attempted || canRetryCreation) ? <button type="button" onClick={reset}>Clear draft</button> : null}
          <form className="easy-onboard-form" onSubmit={handleSubmit}>
            <section className="easy-onboard-form-section">
              <div className="easy-onboard-section-heading">
                <div>
                  <Users aria-hidden="true" />
                  <h2>Recipients</h2>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setRecipients((current) => [
                      ...current,
                      blankRecipient(),
                    ])
                  }
                  disabled={locked || recipients.length >= 20}
                >
                  <Plus aria-hidden="true" /> ADD USER
                </button>
              </div>
              <div className="easy-onboard-rows">
                {recipients.map((recipient, index) => (
                  <div className="easy-onboard-person-row" key={recipient.id}>
                    <label>
                      <span>NAME</span>
                      <input
                        autoComplete="name"
                        value={recipient.name}
                        onChange={(event) =>
                          updateRecipient(
                            recipient.id,
                            "name",
                            event.target.value,
                          )
                        }
                        placeholder="Jane Smith"
                        maxLength={100}
                        required
                        disabled={locked}
                        autoFocus={index === 0}
                      />
                    </label>
                    <label>
                      <span>EMAIL</span>
                      <input
                        type="email"
                        autoComplete="email"
                        value={recipient.email}
                        onChange={(event) =>
                          updateRecipient(
                            recipient.id,
                            "email",
                            event.target.value,
                          )
                        }
                        placeholder="jane@example.com"
                        maxLength={254}
                        required
                        disabled={locked}
                      />
                    </label>
                    <button
                      className="easy-onboard-remove"
                      type="button"
                      aria-label={`Remove recipient ${index + 1}`}
                      disabled={locked || recipients.length === 1}
                      onClick={() =>
                        setRecipients((current) =>
                          current.filter(
                            (currentRecipient) =>
                              currentRecipient.id !== recipient.id,
                          ),
                        )
                      }
                    >
                      <Trash2 aria-hidden="true" />
                    </button>
                  </div>
                ))}
              </div>
            </section>

            <section className="easy-onboard-form-section">
              <div className="easy-onboard-section-heading">
                <div>
                  <Database aria-hidden="true" />
                  <h2>Organization</h2>
                </div>
              </div>
              <div className="easy-onboard-mode-switch">
                <button
                  className={organizationMode === "new" ? "is-active" : ""}
                  type="button"
                  disabled={locked}
                  onClick={() => setOrganizationMode("new")}
                >
                  NEW ORGANIZATION
                </button>
                <button
                  className={
                    organizationMode === "existing" ? "is-active" : ""
                  }
                  type="button"
                  disabled={locked}
                  onClick={() => setOrganizationMode("existing")}
                >
                  EXISTING ORGANIZATION
                </button>
              </div>
              {organizationMode === "new" ? (
                <label>
                  <span>NEW ORGANIZATION NAME</span>
                  <input
                    autoComplete="organization"
                    value={organizationName}
                    onChange={(event) =>
                      setOrganizationName(event.target.value)
                    }
                    placeholder="Example Capital"
                    maxLength={100}
                    required
                    disabled={locked}
                  />
                </label>
              ) : (
                <label>
                  <span>SELECT ORGANIZATION</span>
                  <select
                    value={organizationId}
                    onChange={(event) => setOrganizationId(event.target.value)}
                    required
                    disabled={locked}
                  >
                    <option value="">Choose an existing organization...</option>
                    {options.data?.organizations.map(
                      (organization: {
                        id: string;
                        name: string;
                        slug: string;
                      }) => (
                        <option key={organization.id} value={organization.id}>
                          {organization.name} ({organization.slug})
                        </option>
                      ),
                    )}
                  </select>
                </label>
              )}
            </section>

            <section className="easy-onboard-form-section">
              <div className="easy-onboard-section-heading">
                <div>
                  <KeyRound aria-hidden="true" />
                  <h2>Trial access</h2>
                </div>
                <small>V2.0 · API and dataset trials</small>
              </div>
              <label>
                <span>PRODUCTS TO TRIAL</span>
                <select value={product} disabled={locked} onChange={(event) => {
                  const nextProduct = event.target.value as EasyOnboardProduct;
                  setProduct(nextProduct);
                  if (nextProduct === "world-tickers") {setWebApi(false); setDatasetMode("live");}
                }}>
                  <option value="world">World V2.0</option>
                  <option value="world-tickers">World V2.0 (ticker-only)</option>
                  <option value="atlas">Atlas V2.0</option>
                  <option value="world-atlas">World V2.0 + Atlas V2.0</option>
                </select>
              </label>
              <label className="easy-onboard-duration">
                <span>TRIAL DURATION (DAYS)</span>
                <input
                  type="number"
                  min={1}
                  max={365}
                  step={1}
                  value={trialDaysInput}
                  onChange={(event) =>
                    setTrialDaysInput(event.currentTarget.value)
                  }
                  required
                  disabled={locked}
                />
                <small>
                  The subscription and API key expire
                  together after this many days.
                </small>
              </label>
              <div className="easy-onboard-access-grid">
                <label className={webApi ? "is-selected" : ""}>
                  <input
                    type="checkbox"
                    checked={webApi}
                    disabled={locked || product === "world-tickers"}
                    onChange={(event) => setWebApi(event.target.checked)}
                  />
                  <KeyRound aria-hidden="true" />
                  <strong>Web API</strong>
                  <span>{product === "world-tickers" ? "Ticker-only API access is not available" : `${trialProductLabel(product)} on docs.nosible.com`}</span>
                </label>
                <label className={staticAvailable ? staticS3 ? "is-selected" : "" : "is-disabled"}>
                  <input type="checkbox" checked={staticS3} disabled={locked || !staticAvailable}
                    onChange={(event) => setDatasetMode(event.target.checked ? "static" : "none")} />
                  <Database aria-hidden="true" />
                  <strong>Historic Only</strong>
                  <span>{staticAvailable ? "Read-only S3 dataset credentials" : "Not configured for this product"}</span>
                </label>
                <label className={liveAvailable ? liveS3 ? "is-selected" : "" : "is-disabled"}>
                  <input type="checkbox" checked={liveS3} disabled={locked || !liveAvailable || product === "world-tickers"}
                    onChange={(event) => setDatasetMode(event.target.checked ? "live" : "none")} />
                  <Radio aria-hidden="true" />
                  <strong>Historic + Current</strong>
                  <span>{liveAvailable ? "Read-only S3 dataset credentials" : "Not configured for this product"}</span>
                </label>
              </div>
            </section>

            <section className="easy-onboard-form-section">
              <div className="easy-onboard-section-heading">
                <div>
                  <MailCheck aria-hidden="true" />
                  <h2>Email CC</h2>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setCustomCc((current) => [
                      ...current,
                      {id: rowId(), email: ""},
                    ])
                  }
                  disabled={locked || customCc.length >= 20}
                >
                  <Plus aria-hidden="true" /> ADD CC
                </button>
              </div>
              <div className="easy-onboard-default-cc">
                {(options.data?.defaultCc ?? DEFAULT_CC).map((email) => (
                  <span key={email}>{email} - DEFAULT</span>
                ))}
              </div>
              {customCc.map((cc, index) => (
                <div className="easy-onboard-cc-row" key={cc.id}>
                  <label>
                    <span>ADDITIONAL CC {index + 1}</span>
                    <input
                      type="email"
                      value={cc.email}
                      onChange={(event) =>
                        setCustomCc((current) =>
                          current.map((currentCc) =>
                            currentCc.id === cc.id
                              ? {...currentCc, email: event.target.value}
                              : currentCc,
                          ),
                        )
                      }
                      placeholder="colleague@example.com"
                      maxLength={254}
                      required
                      disabled={locked}
                    />
                  </label>
                  <button
                    className="easy-onboard-remove"
                    type="button"
                    aria-label={`Remove CC ${index + 1}`}
                    disabled={locked}
                    onClick={() =>
                      setCustomCc((current) =>
                        current.filter(
                          (currentCc) => currentCc.id !== cc.id,
                        ),
                      )
                    }
                  >
                    <Trash2 aria-hidden="true" />
                  </button>
                </div>
              ))}
            </section>

            {options.error ? (
              <p className="easy-onboard-error" role="alert">
                {options.error.message}
              </p>
            ) : null}
            {onboard.error && !result ? (
              <p className="easy-onboard-error" role="alert">
                {onboard.error.message}
              </p>
            ) : null}

            {result ? (
              <section className="easy-onboard-created">
                <div>
                  <Check aria-hidden="true" />
                  <div>
                    <strong>Access created for {result.organizationName}</strong>
                    <span>
                      Access is saved. The form is locked so the
                      reviewed draft stays matched to these credentials.
                    </span>
                  </div>
                </div>
                <div className="easy-onboard-credential-grid">
                  {result.apiKey ? (
                    <article>
                      <span>NOSIBLE WEB API KEY</span>
                      <code>{result.apiKey}</code>
                      <button
                        type="button"
                        onClick={() => void copyText("api", result.apiKey!)}
                      >
                        {copied === "api" ? <Check /> : <Clipboard />}
                        {copied === "api" ? "COPIED" : "COPY"}
                      </button>
                    </article>
                  ) : null}
                  {result.awsAccessKeyId && result.awsSecretAccessKey ? (
                    <article>
                      <span>READ-ONLY AWS CREDENTIALS</span>
                      <code>{result.awsAccessKeyId}</code>
                      <code>{result.awsSecretAccessKey}</code>
                      <small>
                        {result.buckets
                          .map((bucket: {name: string}) => bucket.name)
                          .join(" + ")}
                      </small>
                      <button
                        type="button"
                        onClick={() =>
                          void copyText(
                            "aws",
                            `AWS_ACCESS_KEY_ID=${result.awsAccessKeyId}\nAWS_SECRET_ACCESS_KEY=${result.awsSecretAccessKey}\nAWS_REGION=${result.buckets[0]?.region ?? "us-east-1"}`,
                          )
                        }
                      >
                        {copied === "aws" ? <Check /> : <Clipboard />}
                        {copied === "aws" ? "COPIED" : "COPY"}
                      </button>
                    </article>
                  ) : null}
                </div>
                <button
                  className="easy-onboard-again"
                  type="button"
                  onClick={reset}
                >
                  Create another access package
                </button>
              </section>
            ) : (
              <>
                <button
                  className="easy-onboard-submit"
                  type="submit"
                  disabled={
                    Boolean(storageError) || (attempted && !canRetryCreation) || onboard.isPending ||
                    !recipientsComplete ||
                    !organizationComplete ||
                    !trialDaysValid ||
                    (!validTrialAccess({webApi, staticS3, liveS3}) || !datasetAvailable)
                  }
                >
                  {onboard.isPending ? (
                    <>
                      <LoaderCircle className="easy-onboard-spinner" />
                      CREATING ACCESS...
                    </>
                  ) : (
                    <>
                      <Rocket aria-hidden="true" />
                      CREATE ACCESS
                    </>
                  )}
                </button>
              </>
            )}
          </form>
        </div>

        <aside className="easy-onboard-email-column">
          <article className="easy-onboard-email-card">
            <header className="easy-onboard-email-toolbar">
              <div>
                <span>EMAIL</span>
                <h2>{result ? "Ready to send" : "Live preview"}</h2>
                <small>
                  {result
                    ? "Real credentials are now included."
                    : "Credential placeholders are shown until access is created."}
                </small>
              </div>
              <div className="easy-onboard-email-tabs" role="tablist">
                <button
                  className={emailView === "preview" ? "is-active" : ""}
                  type="button"
                  role="tab"
                  aria-selected={emailView === "preview"}
                  onClick={() => setEmailView("preview")}
                >
                  <Eye aria-hidden="true" /> PREVIEW
                </button>
                <button
                  className={emailView === "edit" ? "is-active" : ""}
                  type="button"
                  role="tab"
                  aria-selected={emailView === "edit"}
                  onClick={() => setEmailView("edit")}
                >
                  <PencilLine aria-hidden="true" /> EDIT
                </button>
              </div>
            </header>

            <div className="easy-onboard-preview-heading">
              <div>
                <span>SUBJECT</span>
                <strong>{currentEmailSubject || "Subject required"}</strong>
                <small>To: {previewTo.join(", ")}</small>
                <small>CC: {previewCc.join(", ")}</small>
              </div>
              <small>Gmail signature is appended when sent.</small>
            </div>

            {emailView === "preview" ? (
              <div
                className="easy-onboard-preview-body"
                role="tabpanel"
                dangerouslySetInnerHTML={{
                  __html: renderEditableEmailHtml(currentEmailBody),
                }}
              />
            ) : (
              <div className="easy-onboard-email-editor" role="tabpanel">
                <label>
                  <span>SUBJECT</span>
                  <input
                    value={currentEmailSubject}
                    onChange={(event) => setEmailSubject(event.target.value)}
                    maxLength={200}
                    disabled={emailSent || sendingEmail || deliveryUnknown}
                  />
                </label>
                <label>
                  <span>MESSAGE</span>
                  <textarea
                    value={currentEmailBody}
                    onChange={(event) => setEmailBody(event.target.value)}
                    maxLength={50_000}
                    rows={30}
                    disabled={emailSent || sendingEmail || deliveryUnknown}
                  />
                </label>
                <div className="easy-onboard-editor-help">
                  <p>
                    Use * for bullets and two leading spaces plus - for nested
                    bullets. Changes appear in Preview immediately.
                  </p>
                  {emailCustomized ? (
                    <button
                      type="button"
                      onClick={() => {
                        setEmailSubject(null);
                        setEmailBody(null);
                      }}
                    >
                      RESET TO GENERATED DRAFT
                    </button>
                  ) : null}
                </div>
              </div>
            )}
          </article>

          {result ? (
            <section className="easy-onboard-delivery">
              {emailSent ? (
                <div className="easy-onboard-sent" role="status">
                  <MailCheck aria-hidden="true" />
                  <div>
                    <strong>EMAIL SENT</strong>
                    <span>
                      {sendEmail.data ? <>
                        Sent from {sendEmail.data.from} to{" "}
                        {sendEmail.data.to.join(", ")} after verifying{" "}
                        {sendEmail.data.verification.api.endpoints.length} API
                        endpoints and{" "}
                        {sendEmail.data.verification.s3.buckets.length} S3
                        bucket{sendEmail.data.verification.s3.buckets.length === 1 ? "" : "s"}.
                      </> : "The server confirmed successful delivery after verifying access."}
                    </span>
                  </div>
                </div>
              ) : sendConfirmationOpen ? (
                <div
                  className="easy-onboard-send-confirm"
                  role="alertdialog"
                  aria-labelledby="easy-onboard-send-confirm-title"
                  aria-describedby="easy-onboard-send-confirm-description"
                >
                  <span>CONFIRM EMAIL</span>
                  <h3 id="easy-onboard-send-confirm-title">
                    Send from {gmail?.email ?? "stuart@nosible.com"}?
                  </h3>
                  <p id="easy-onboard-send-confirm-description">
                    This sends one email to {result.email.to.join(", ")} and
                    CCs {result.email.cc.join(", ")}.
                  </p>
                  <dl>
                    <div>
                      <dt>SUBJECT</dt>
                      <dd>{currentEmailSubject}</dd>
                    </div>
                  </dl>
                  <div className="easy-onboard-send-confirm__actions">
                    <button
                      type="button"
                      onClick={() => setSendConfirmationOpen(false)}
                    >
                      CANCEL
                    </button>
                    <button type="button" onClick={confirmSend}>
                      <Send aria-hidden="true" /> YES, SEND EMAIL
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  className="easy-onboard-send"
                  type="button"
                  disabled={
                    !gmailReady ||
                    sendingEmail ||
                    deliveryUnknown ||
                    !currentEmailSubject.trim() ||
                    !currentEmailBody.trim()
                  }
                  onClick={handleSend}
                >
                  {sendingEmail ? (
                    <LoaderCircle className="easy-onboard-spinner" />
                  ) : (
                    <Send aria-hidden="true" />
                  )}
                  {sendingEmail
                    ? recoveringDelivery ? "CHECKING DELIVERY STATUS..." : "VERIFYING ACCESS & SENDING..."
                    : deliveryUnknown ? "DELIVERY NEEDS CONFIRMATION"
                    : `REVIEW & SEND FROM ${(gmail?.email ?? "stuart@nosible.com").toUpperCase()}`}
                </button>
              )}
              {!emailSent ? (
                <p className="easy-onboard-progress">
                  Selected V2.0 API endpoints are verified before sending.
                  S3 credentials are checked only when S3 delivery is selected.
                </p>
              ) : null}
              {!emailSent && deliveryError ? (
                <p className="easy-onboard-error" role="alert">
                  {deliveryError}
                </p>
              ) : null}
            </section>
          ) : (
            <div className="easy-onboard-preview-notice">
              <KeyRound aria-hidden="true" />
              <p>
                <strong>No credentials have been created.</strong>
                The masked values above are placeholders. Configure the form,
                review this email, then use Create Access when ready.
              </p>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
